import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  MenuItem,
  Menu,
  IconButton,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Search,
  FileText,
  Filter,
  Calendar,
  Download,
  Printer,
  FileOutput,
  ArrowLeft,
  HelpCircle,
  Layers,
  RotateCw,
  Settings,
  X,
  ChevronDown,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import GSTReportTable from "./Components/GstReportComponents/GSTReportTable";
import { dummyGSTData } from "../../../dummyData";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const GSTReportDetailsScreen = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [gstPeriod, setGstPeriod] = useState("monthly");
  const [anchorEl, setAnchorEl] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    invoiceNo: true,
    date: true,
    firmName: true,
    gstin: true,
    taxableAmount: true,
    cgst: true,
    sgst: true,
    igst: true,
    totalAmount: true,
  });

  // Custom date range states
  const [openDateDialog, setOpenDateDialog] = useState(false);
  const [startDate, setStartDate] = useState(new Date("2023-07-01"));
  const [endDate, setEndDate] = useState(new Date("2023-07-31"));
  const [customDateLabel, setCustomDateLabel] = useState("");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleExportClick = event => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleCloseExport = () => {
    setExportAnchorEl(null);
  };

  const handleColumnToggle = columnId => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleRefresh = () => {
    setIsLoading(true);
    // Simulate data refresh
    setTimeout(() => {
      setIsLoading(false);
    }, 800);
  };

  const handleGstPeriodChange = event => {
    const value = event.target.value;
    setGstPeriod(value);

    if (value === "custom") {
      setOpenDateDialog(true);
    }
  };

  const handleOpenDateDialog = () => {
    setOpenDateDialog(true);
  };

  const handleCloseDateDialog = () => {
    // If canceling without selecting, revert to previous selection
    if (gstPeriod === "custom" && !customDateLabel) {
      setGstPeriod("monthly");
    }
    setOpenDateDialog(false);
  };

  const handleApplyCustomDates = () => {
    // Format dates for display
    const formatDate = date => {
      return date
        .toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "/");
    };

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    setCustomDateLabel(`${formattedStartDate} - ${formattedEndDate}`);
    setOpenDateDialog(false);
  };

  const periods = [
    { value: "monthly", label: "Monthly (Jul 2023)" },
    { value: "quarterly", label: "Quarterly (Jul-Sep 2023)" },
    { value: "yearly", label: "Yearly (FY 2023-24)" },
    { value: "custom", label: "Custom Range" },
  ];

  const getDisplayPeriod = () => {
    switch (gstPeriod) {
      case "monthly":
        return "01/07/2023 - 31/07/2023";
      case "quarterly":
        return "01/07/2023 - 30/09/2023";
      case "yearly":
        return "01/04/2023 - 31/03/2024";
      case "custom":
        return customDateLabel || "Custom Date Range";
      default:
        return "01/07/2023 - 31/07/2023";
    }
  };

  return (
    <div className="bg-white overflow-auto">
      {/* Header Section */}
      <div className="px-6 py-4 border-b">
        {/* Title and Navigation Row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <NavLink to="/report/gstreport" className="mr-4">
              <IconButton className="hover:bg-gray-100" size="small">
                <ArrowLeft size={20} />
              </IconButton>
            </NavLink>
            <h1 className="text-2xl font-semibold font-outfit text-gray-800">
              GST Report
            </h1>
            <div className="ml-4 bg-[#F5EDFD] px-3 py-1 rounded-md flex items-center text-[#5652B0] border border-[#5652B0] border-opacity-20">
              <Calendar size={16} className="mr-2" />
              <span className="text-sm font-medium">{getDisplayPeriod()}</span>
            </div>
          </div>

          {/* Help Icon */}
          <div className="flex items-center gap-2">
            <Tooltip title="GST Help">
              <IconButton className="hover:bg-gray-100">
                <HelpCircle size={18} className="text-gray-600" />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* Controls Row */}
        <div className="flex items-center justify-between flex-wrap">
          {/* Search Bar */}
          <div className="w-[350px]">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by invoice, firm, or GSTIN..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                  <X size={16} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-4">
            {/* Period Filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>GST Period</InputLabel>
              <Select
                value={gstPeriod}
                label="GST Period"
                onChange={handleGstPeriodChange}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#5652B0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#5652B0",
                  },
                }}>
                {periods.map(period => (
                  <MenuItem key={period.value} value={period.value}>
                    {period.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <IconButton
                onClick={handleRefresh}
                className="hover:bg-gray-100"
                disabled={isLoading}
                title="Refresh">
                <RotateCw
                  size={20}
                  className={`text-gray-600 ${isLoading ? "animate-spin" : ""}`}
                />
              </IconButton>
              <IconButton
                onClick={handleSettingsClick}
                className="hover:bg-gray-100"
                title="Column Settings">
                <Settings size={20} className="text-gray-600" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseSettings}
                PaperProps={{
                  style: {
                    maxHeight: 300,
                    width: 180,
                  },
                }}>
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-600">
                    Show Columns
                  </p>
                </div>
                {Object.keys(visibleColumns).map(columnId => (
                  <MenuItem
                    key={columnId}
                    onClick={() => handleColumnToggle(columnId)}
                    dense>
                    <input
                      type="checkbox"
                      checked={visibleColumns[columnId]}
                      readOnly
                      className="mr-2 rounded-sm border-gray-300 text-[#5652B0] focus:ring-[#5652B0]"
                    />
                    <span className="text-sm capitalize">
                      {columnId === "gstin"
                        ? "GSTIN"
                        : columnId === "cgst"
                        ? "CGST"
                        : columnId === "sgst"
                        ? "SGST"
                        : columnId === "igst"
                        ? "IGST"
                        : columnId
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, str => str.toUpperCase())}
                    </span>
                  </MenuItem>
                ))}
              </Menu>
            </div>

            {/* Export Button */}
            <button
              onClick={handleExportClick}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-[#5652B0] text-[#5652B0] hover:bg-[#5652B0] hover:text-white transition-all text-sm font-medium">
              <FileOutput size={16} />
              <span>Export</span>
            </button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={handleCloseExport}
              PaperProps={{
                style: {
                  width: 180,
                },
              }}>
              <MenuItem onClick={handleCloseExport} dense>
                <Download size={16} className="mr-2" />
                <span className="text-sm">Excel (.xlsx)</span>
              </MenuItem>
              <MenuItem onClick={handleCloseExport} dense>
                <Download size={16} className="mr-2" />
                <span className="text-sm">CSV File</span>
              </MenuItem>
              <MenuItem onClick={handleCloseExport} dense>
                <FileText size={16} className="mr-2" />
                <span className="text-sm">PDF Document</span>
              </MenuItem>
              <MenuItem onClick={handleCloseExport} dense>
                <Printer size={16} className="mr-2" />
                <span className="text-sm">Print Report</span>
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>

      {/* Custom Date Range Dialog */}
      <Dialog open={openDateDialog} onClose={handleCloseDateDialog}>
        <DialogTitle>
          <div className="font-outfit font-semibold text-lg text-gray-800">
            Select Custom Date Range
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="mt-2 mb-4">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <div className="flex flex-col gap-4">
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={newValue => setStartDate(newValue)}
                  renderInput={params => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                  inputFormat="dd/MM/yyyy"
                  className="w-full"
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={newValue => setEndDate(newValue)}
                  renderInput={params => (
                    <TextField {...params} size="small" fullWidth />
                  )}
                  inputFormat="dd/MM/yyyy"
                  minDate={startDate}
                  className="w-full"
                />
              </div>
            </LocalizationProvider>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDateDialog}
            sx={{
              color: "#4B5563",
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleApplyCustomDates}
            sx={{
              backgroundColor: "#5652B0",
              color: "white",
              "&:hover": { backgroundColor: "#4A47A3" },
            }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tabs for GST Report Types */}
      <div className="border-b">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          className="px-6"
          TabIndicatorProps={{
            style: {
              backgroundColor: "#5652B0",
            },
          }}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              color: "#4B5563",
              minWidth: "auto",
              padding: "12px 16px",
              fontFamily: "Outfit",
            },
            "& .Mui-selected": {
              color: "#5652B0",
              fontWeight: 600,
            },
          }}>
          <Tab
            label="Invoice Details"
            icon={<FileText size={16} />}
            iconPosition="start"
          />
          <Tab
            label="Tax Rate Summary"
            icon={<Layers size={16} />}
            iconPosition="start"
          />
          <Tab
            label="GSTR-1"
            icon={<FileText size={16} />}
            iconPosition="start"
          />
          <Tab
            label="GSTR-3B"
            icon={<FileText size={16} />}
            iconPosition="start"
          />
          <Tab
            label="HSN Summary"
            icon={<FileText size={16} />}
            iconPosition="start"
          />
        </Tabs>
      </div>

      {/* Content Area */}
      <div className="px-6 py-4">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <CircularProgress size={40} style={{ color: "#5652B0" }} />
          </div>
        ) : (
          <>
            {activeTab === 0 && (
              <GSTReportTable
                searchQuery={searchQuery}
                visibleColumns={visibleColumns}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                showTotals={true}
              />
            )}
            {activeTab === 1 && (
              <div className="bg-white rounded-lg">
                <div className="flex items-center mb-4">
                  <FileText size={20} className="text-[#5652B0] mr-2" />
                  <h2 className="font-semibold text-lg font-outfit text-gray-800">
                    GST Tax Rate Summary
                  </h2>
                </div>

                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: "1px solid #E5E7EB", borderRadius: "8px" }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Tax Rate</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Taxable Value
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          CGST Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          SGST Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          IGST Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Total Tax
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dummyGSTData?.taxRateSummary.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <strong>{item.taxRate}</strong>
                          </TableCell>
                          <TableCell>₹{item.taxableValue.toFixed(2)}</TableCell>
                          <TableCell>₹{item.cgstAmount.toFixed(2)}</TableCell>
                          <TableCell>₹{item.sgstAmount.toFixed(2)}</TableCell>
                          <TableCell>₹{item.igstAmount.toFixed(2)}</TableCell>
                          <TableCell>₹{item.totalTax.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          ₹
                          {dummyGSTData?.taxRateSummaryTotal.taxableValue.toFixed(
                            2,
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          ₹
                          {dummyGSTData?.taxRateSummaryTotal.cgstAmount.toFixed(
                            2,
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          ₹
                          {dummyGSTData?.taxRateSummaryTotal.sgstAmount.toFixed(
                            2,
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          ₹
                          {dummyGSTData?.taxRateSummaryTotal.igstAmount.toFixed(
                            2,
                          )}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          ₹
                          {dummyGSTData?.taxRateSummaryTotal.totalTax.toFixed(
                            2,
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
            {activeTab === 2 && (
              <div className="bg-white rounded-lg">
                <div className="flex items-center mb-4">
                  <FileText size={20} className="text-[#5652B0] mr-2" />
                  <h2 className="font-semibold text-lg font-outfit text-gray-800">
                    GSTR-1 Format
                  </h2>
                </div>
                <div className="flex gap-4 mb-4">
                  <Chip
                    label="B2B Invoices"
                    onClick={() => {}}
                    sx={{
                      backgroundColor: "#EAD8FC",
                      color: "#5652B0",
                      fontWeight: 500,
                      "&:hover": { backgroundColor: "#E0C6FB" },
                    }}
                  />
                  <Chip
                    label="B2C Invoices"
                    onClick={() => {}}
                    sx={{
                      backgroundColor: "#F9FAFB",
                      "&:hover": { backgroundColor: "#F3F4F6" },
                    }}
                  />
                  <Chip
                    label="Credit/Debit Notes"
                    onClick={() => {}}
                    sx={{
                      backgroundColor: "#F9FAFB",
                      "&:hover": { backgroundColor: "#F3F4F6" },
                    }}
                  />
                </div>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: "1px solid #E5E7EB", borderRadius: "8px" }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                        <TableCell sx={{ fontWeight: 600 }}>GSTIN</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Firm Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Invoice Number
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Invoice Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Invoice Value
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Place of Supply
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Tax Rate</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Taxable Value
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dummyGSTData?.gstr1Data.length > 0 ? (
                        dummyGSTData?.gstr1Data.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{item.gstin}</TableCell>
                            <TableCell>{item.firmName}</TableCell>
                            <TableCell>{item.invoiceNumber}</TableCell>
                            <TableCell>{item.invoiceDate}</TableCell>
                            <TableCell>
                              ₹{item.invoiceValue.toFixed(2)}
                            </TableCell>
                            <TableCell>{item.placeOfSupply}</TableCell>
                            <TableCell>{item.taxRate}</TableCell>
                            <TableCell>
                              ₹{item.taxableValue.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            className="py-8 text-center text-gray-500">
                            No data available for GSTR-1 report
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
            {activeTab === 3 && (
              <div className="bg-white rounded-lg">
                <div className="flex items-center mb-4">
                  <FileText size={20} className="text-[#5652B0] mr-2" />
                  <h2 className="font-semibold text-lg font-outfit text-gray-800">
                    GSTR-3B Format
                  </h2>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium mb-2">
                      3.1 Details of Outward Supplies
                    </h3>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{ border: "1px solid #E5E7EB", borderRadius: "8px" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Nature of Supplies
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Total Taxable Value
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Integrated Tax
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Central Tax
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              State/UT Tax
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dummyGSTData?.gstr3bData.outwardSupplies.map(
                            (item, index) => (
                              <TableRow key={index} hover>
                                <TableCell>{item.natureOfSupplies}</TableCell>
                                <TableCell>
                                  ₹{item.taxableValue.toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  ₹{item.integratedTax.toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  ₹{item.centralTax.toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  ₹{item.stateTax.toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ),
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium mb-2">
                      3.2 Details of Inter-State Supplies
                    </h3>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{ border: "1px solid #E5E7EB", borderRadius: "8px" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Place of Supply
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Taxable Value
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              Amount of Integrated Tax
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dummyGSTData?.gstr3bData.interStateSupplies.length >
                          0 ? (
                            dummyGSTData?.gstr3bData.interStateSupplies.map(
                              (item, index) => (
                                <TableRow key={index} hover>
                                  <TableCell>{item.placeOfSupply}</TableCell>
                                  <TableCell>
                                    ₹{item.taxableValue.toFixed(2)}
                                  </TableCell>
                                  <TableCell>
                                    ₹{item.integratedTax.toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ),
                            )
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={3}
                                className="py-4 text-center text-gray-500">
                                No data available
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 4 && (
              <div className="bg-white rounded-lg">
                <div className="flex items-center mb-4">
                  <FileText size={20} className="text-[#5652B0] mr-2" />
                  <h2 className="font-semibold text-lg font-outfit text-gray-800">
                    HSN Summary
                  </h2>
                </div>
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: "1px solid #E5E7EB", borderRadius: "8px" }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
                        <TableCell sx={{ fontWeight: 600 }}>HSN</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Description
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>UQC</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Total Quantity
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Total Value
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Taxable Value
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Integrated Tax
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Central Tax
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          State/UT Tax
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dummyGSTData?.hsnSummary.length > 0 ? (
                        dummyGSTData?.hsnSummary.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{item.hsn}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>{item.uqc}</TableCell>
                            <TableCell>{item.totalQuantity}</TableCell>
                            <TableCell>₹{item.totalValue.toFixed(2)}</TableCell>
                            <TableCell>
                              ₹{item.taxableValue.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              ₹{item.integratedTax.toFixed(2)}
                            </TableCell>
                            <TableCell>₹{item.centralTax.toFixed(2)}</TableCell>
                            <TableCell>₹{item.stateTax.toFixed(2)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={9}
                            className="py-8 text-center text-gray-500">
                            No HSN summary data available for the selected
                            period
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GSTReportDetailsScreen;
